import React from 'react';
import styled from '@emotion/styled';
import { useRecoilValue } from 'recoil';
import Span from '../../../../common/text/Span';
import { BLACK, GRAY } from '../../../../../styles/constants/color';
import { otherBankListState } from '../../../../../data/estimate/common/otherBank/list/selector';
import { OtherBank } from '../../../../../types/estimate/common/OtherBank';
import OtherBankItemRow from './row/OtherBankItemRow';
import OtherBankItemDummyRow from './row/OtherBankItemDummyRow';
import useCalculatorForPaymentWay from '../../../../../hooks/estimate/common/paymentWay/useCalculatorForPaymentWay';
import { MakerFinance } from '../../../../../types/estimate/common/PaymentWay';
import { otherBankInfoState } from '../../../../../data/estimate/common/otherBank/info/selector';
import useDevice from '../../../../../hooks/common/useDevice';

const Base = styled.div`
  max-height: 551px;
  overflow-y: overlay;

  @media (max-width: 768px) {
    max-height: unset;
    overflow-y: hidden;
  }
`;

const Table = styled.table`
  width: 705px;
  height: 551px;

  border-top: 1px solid ${BLACK};
  border: 1px solid ${GRAY};

  & th,
  & td {
    height: 50px;
    border-bottom: 1px solid ${GRAY};
    border-right: 1px solid ${GRAY};
  }

  & th {
    &:nth-of-type(6),
    &:nth-of-type(7) {
      width: 75px;
    }
  }

  & td {
    text-align: center;

    &:first-of-type {
      width: 65px;
      cursor: pointer;
    }

    &:nth-of-type(2) {
      background-color: #eaf5ff;
    }

    &:nth-of-type(3),
    &:nth-of-type(4) {
      width: 115px;
    }

    & > span {
      display: block;
    }
  }

  @media (max-width: 768px) {
    width: 100%;

    & td {
      &:first-of-type {
        width: 40px;
      }

      &:nth-of-type(3) {
        width: 75px;
      }
      &:nth-of-type(4) {
        width: 80px;
      }
      &:nth-of-type(5) {
        min-width: 60px;
      }
    }
  }
`;

interface Props {
  openCm: boolean;
  closeEvent: () => void;
}

/**
 *  타금융사 할부 메이커금융 상품 목록 테이블 컴포넌트
 * @param {boolean} openCm CM 펼치기 여부
 * @param {function} closeEvent 모달 닫기 이벤트 함수
 *  */
function OtherBankTable({ openCm, closeEvent }: Props): JSX.Element {
  // device
  const device: string = useDevice();
  // select
  const targetTable: number = useRecoilValue(otherBankInfoState({ name: 'targetTable' }));

  // list
  const otherBankList: OtherBank[] = useRecoilValue(
    otherBankListState({ name: 'otherBankMakerFinance' })
  );
  const dummyList: number[] = Array.from({ length: 10 - otherBankList.length });

  // function
  const { updateDefaultSettings } = useCalculatorForPaymentWay(targetTable);
  const onClick = (item: OtherBank): void => {
    const product: MakerFinance = item.param;
    updateDefaultSettings(product, undefined, true);
    closeEvent();
  };
  return (
    <Base>
      <Table summary='타금융사 할부 메이커금융 상품 목록 테이블'>
        <thead>
          <tr>
            <th></th>
            <th>
              <Span fontSize='15px'>상품명</Span>
            </th>
            <th>
              {device === 'pc' ? (
                <Span fontSize='15px'>월납입금</Span>
              ) : (
                <>
                  <Span fontSize='15px'>총 이자</Span>
                  <br />
                  <Span fontSize='15px'>월납입금</Span>
                </>
              )}
            </th>
            <th>
              {device === 'pc' ? (
                <Span fontSize='15px'>총 이자</Span>
              ) : (
                <>
                  <Span fontSize='15px'>금리</Span>
                  <br />
                  <Span fontSize='15px'>고객캐시백</Span>
                </>
              )}
            </th>
            {device === 'pc' && (
              <>
                <th>
                  <Span fontSize='15px'>금리</Span>
                </th>
                <th>
                  <Span fontSize='15px'>고객캐시백</Span>
                </th>
              </>
            )}
            {openCm && (
              <th>
                <Span fontSize='15px'>CM</Span>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {!!otherBankList.length &&
            otherBankList.map((item: OtherBank) => (
              <OtherBankItemRow
                key={item.id}
                item={item}
                onClick={onClick}
                openCm={openCm}
                device={device}
              />
            ))}
          {!!dummyList.length &&
            dummyList.map((item: number) => (
              <OtherBankItemDummyRow
                key={isNaN(item) ? Math.random() : -item}
                openCm={openCm}
                device={device}
              />
            ))}
        </tbody>
      </Table>
    </Base>
  );
}

export default OtherBankTable;
